import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import { Redirect, useLocation } from 'react-router';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { useGraphRef } from '../hooks/useGraphRef';
import { launchesRouteConfig } from '../routes';

import { LaunchesEmptyState } from './launchHistory/launchesEmptyState/LaunchesEmptyState';
import {
  LaunchHistory,
  LaunchHistoryGraphVariantFragment,
} from './launchHistory/LaunchHistory';

const LaunchesPageQuery = gql`
  query LaunchesPageQuery(
    $serviceId: ID!
    $graphVariant: String!
    $skipOrder: Boolean!
  ) {
    service(id: $serviceId) {
      id
      variant(name: $graphVariant) {
        id
        launchHistory {
          id
        }
        ...LaunchHistoryGraphVariantFragment
      }
    }
  }
  ${LaunchHistoryGraphVariantFragment}
`;

export const LaunchesPage = ({
  isCloudSupergraph,
}: {
  isCloudSupergraph: Boolean;
}) => {
  const location = useLocation();
  const graphRef = useGraphRef();
  const { launchId } = useRouteParams(launchesRouteConfig);
  const prevSuccessfulResult = useRef<GraphQLTypes.LaunchesPageQuery>();

  const { data, loading } = useQuery<
    GraphQLTypes.LaunchesPageQuery,
    GraphQLTypes.LaunchesPageQueryVariables
  >(LaunchesPageQuery, {
    variables: {
      serviceId: graphRef?.graphId as string,
      graphVariant: graphRef?.graphVariant as string,
      skipOrder: !isCloudSupergraph,
    },
    pollInterval: 5000,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  useEffect(() => {
    if (data) prevSuccessfulResult.current = data;
  }, [data]);

  useTrackL1PageView('launchesL1', { graphRef });

  // This is a polling query so show spinner and retry until data is returned
  // then ignore errors and use saved state
  if (loading || (!prevSuccessfulResult.current && !data)) {
    return <Loading />;
  }

  const variant = (data || prevSuccessfulResult.current)?.service?.variant;

  if (!variant) {
    return <NotFound />;
  }

  if (!isMinLengthArray(1, variant.launchHistory)) {
    return (
      <PageLayout title={<PageTitle title="Launches" />}>
        <div className="flex h-full justify-center p-8">
          <LaunchesEmptyState />
        </div>
      </PageLayout>
    );
  }

  // redirect to show the details of the first launch if there is not one
  // yet selected
  if (!launchId) {
    return (
      <Redirect
        to={launchesRouteConfig.locationFrom({
          location,
          patch: { launchId: variant.launchHistory[0].id },
        })}
      />
    );
  }

  return (
    <PageLayout title={<PageTitle title="Launches" />}>
      <div className="flex min-h-0 flex-col overflow-hidden p-8 pb-0">
        <LaunchHistory
          variant={variant}
          selectedLaunchId={launchId}
          isCloudSupergraph={isCloudSupergraph}
        />
      </div>
    </PageLayout>
  );
};
